import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Swal from "sweetalert2";
import copyicon from "../assets/copy-svgrepo-com.svg";
import copy from "copy-to-clipboard";
import {
  Container,
  Card,
  CardContent,
  makeStyles,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from "@material-ui/core";
import Navigation from "./Navigation";
import { Pagination, Modal } from "@mui/material";

// ── Auto-fit text ──
// Same component used on ScanQr.js / GenerateQr.js: shrinks font-size (down
// to a floor) so a single line of text never overflows its container, and
// if it still can't fit at the smallest allowed size, wraps onto as many
// lines as needed instead of clipping. Kept identical across all three
// pages so the printed badge looks the same no matter where it's printed
// from.
const FitText = ({
  text,
  maxFontSize = 20,
  minFontSize = 12,
  fontWeight = 400,
  color = "#000",
  style = {},
}) => {
  const containerRef = useRef(null);
  const [renderStyle, setRenderStyle] = useState({
    fontSize: maxFontSize,
    wrapped: false,
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.style.fontSize = `${maxFontSize}px`;
    el.style.whiteSpace = "nowrap";
    el.style.overflow = "hidden";

    let size = maxFontSize;
    el.style.fontSize = `${size}px`;

    while (el.scrollWidth > el.clientWidth && size > minFontSize) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }

    const stillOverflowsAtFloor =
      size === minFontSize && el.scrollWidth > el.clientWidth;

    setRenderStyle({ fontSize: size, wrapped: stillOverflowsAtFloor });
  }, [text, maxFontSize, minFontSize]);

  const { fontSize, wrapped } = renderStyle;

  const overflowStyle = wrapped
    ? {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }
    : {
        whiteSpace: "nowrap",
      };

  return (
    <div
      ref={containerRef}
      style={{
        fontSize: `${fontSize}px`,
        fontWeight,
        color,
        lineHeight: 1.25,
        ...overflowStyle,
        ...style,
      }}
    >
      {text}
    </div>
  );
};

// ── Shared print card layout ──
// Identical layout/behavior to ScanQr.js and GenerateQr.js: Name + secondary
// line (Mobile No • Email) on the left (auto-fit / wrap via FitText), QR
// code on the right, spread edge-to-edge with `justifyContent: space-between`.
// Used for BOTH the on-screen Print Preview popup and the actual printed
// output, so what's shown in the popup is exactly what gets printed.
const PrintCardContent = ({ ticket, qrSize = 150 }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: "16px",
    }}
  >
    <div
      style={{
        textAlign: "left",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "4px",
      }}
    >
      <FitText
        text={ticket?.name || ""}
        maxFontSize={24}
        minFontSize={14}
        fontWeight={700}
        color="#000"
      />
      <FitText
        text={ticket?.subtitle || ""}
        maxFontSize={16}
        minFontSize={11}
        fontWeight={400}
        color="#555"
      />
    </div>
    {ticket?.qrSrc && (
      <img
        src={ticket.qrSrc}
        alt="QR Code"
        width={qrSize}
        height={qrSize}
        style={{ display: "inline-block", flexShrink: 0 }}
      />
    )}
  </div>
);

const UserTable = () => {
  const [uid, setUid] = useState("");

  // ── Active fields (matches the dataset: Firstname, MobileNo, Email, RegistrationNumber, Color) ──
  const [Firstname, setFirstName] = useState("");
  const [RegistrationNumber, setRegistrationNumber] = useState("");
  const [MobileNo, setMobileNo] = useState("");
  const [Email, setEmail] = useState("");
  const [Color, setColor] = useState("");
  const [Day, setDay] = useState("");
  const [Category, setCategory] = useState("");
  const [qrCode, setQrCode] = useState("");

  const [checkIn, setcheckIn] = useState(true);
  const [checkOut, setcheckOut] = useState(true);
  const [exhibitor, setExhibitor] = useState(
    JSON.parse(localStorage.getItem("token")).token,
  );

  // QR dialog state
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDialogData, setQrDialogData] = useState({ src: "", name: "" });

  const handleShowQr = (item) => {
    setQrDialogData({
      src: item.qrCode || item.ticketId || "",
      name: item.Firstname || "",
    });
    setQrDialogOpen(true);
  };

  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
    setTimeout(() => setQrDialogData({ src: "", name: "" }), 300);
  };

  // Print preview popup state — styled like the app, shows the printable card inside
  const [printOpen, setPrintOpen] = useState(false);
  const [printData, setPrintData] = useState({ name: "", subtitle: "", qrSrc: "" });

  const openPrintPreview = (item) => {
    const qrSrc = item.qrCode || item.ticketId || "";
    if (!qrSrc) {
      alert("No QR code available to print");
      return;
    }
    setPrintData({
      name: item.Firstname || "",
      subtitle: [item.MobileNo, item.Email].filter(Boolean).join(" • "),
      qrSrc,
    });
    setPrintOpen(true);
  };

  const closePrintPreview = () => setPrintOpen(false);

  const handlePrintNow = () => {
    window.print();
    closePrintPreview();
  };

  const saveQrCodeDetails = async (item) => {
    let ticketDetails = {
      uid: item._id,
      Firstname: item.Firstname,
      RegistrationNumber: item.RegistrationNumber || "",
      MobileNo: item.MobileNo || "",
      Email: item.Email || "",
      color: item.Color || "",
      Day: item.Day || "",
      Category: item.Category || "",
      qrCode: item.qrCode || item.ticketId || "",
      exhibitor,
      checkIn,
      type: "checkIn",
    };

    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/saveOne", ticketDetails)
      .then((data) => {
        ticketDetails = data.data;
        Swal.fire({
          title: "Success",
          html: `${data.data.message}`,
          icon: "success",
          confirmButtonText: "Close",
          confirmButtonColor: "#e81b23",
          customClass: { popup: "custom-swal-popup" },
        }).then((result) => {
          if (result.isConfirmed) window.location.reload();
        });
        setTimeout(() => {
          window.location.reload();
        }, 2500);
      })
      .catch((error) => console.log(error));
    return ticketDetails;
  };

  const checkOutUser = async (item) => {
    let ticketDetails = { uid: item._id, exhibitor, checkOut };
    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/checkout", ticketDetails)
      .then((data) => {
        ticketDetails = data.data;
        window.location.reload();
      })
      .catch((error) => console.log(error));
    return ticketDetails;
  };

  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [data, setAllUserData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const rowsPerPage = 10;

  const getAllUsers = async () => {
    await axios
      .get(process.env.REACT_APP_ADMIN + "/qrCode/getAllData")
      .then((res) => {
        setAllUserData(res.data);
        setTotalRows(res.data.length);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  const filteredData = data.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => setPage(value);

  const startIndex = (page - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + rowsPerPage,
  );

  return (
    <Container className={classes.conatiner}>
      <div className={classes.bgOrb1}></div>
      <div className={classes.bgOrb2}></div>
      <div className={classes.bgOrb3}></div>

      <Card className={classes.card}>
        <CardContent style={{ padding: 0 }}>
          <Navigation />

          <Grid container>
            <div className={classes.root}>
              {/* Search row */}
              <div className={classes.searchRow}>
                <TextField
                  className={classes.search}
                  variant="outlined"
                  label="Search"
                  value={searchText}
                  onChange={handleSearchTextChange}
                  InputProps={{ style: { color: "#f0f0f5" } }}
                />
                <span className={classes.rowCount}>
                  {filteredData.length} records
                </span>
              </div>

              {/* Scrollable table wrapper */}
              <div className={classes.tableWrapper}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>
                        Name
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Registration No.
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Mobile No.
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Email
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Color
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Day
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Category
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        QR Code
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Check In
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          className={classes.tableRow}
                          style={{ animationDelay: `${index * 0.04}s` }}
                        >
                          <TableCell className={classes.tableCell}>
                            {item.Firstname}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <span className={classes.truncateText}>
                              {item.RegistrationNumber || "—"}
                            </span>
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <span className={classes.cellWithCopy}>
                              <span className={classes.truncateText}>
                                {item.MobileNo || "—"}
                              </span>
                              {item.MobileNo && (
                                <span
                                  onClick={() => copy(item.MobileNo)}
                                  className={classes.copyIcon}
                                >
                                  <img src={copyicon} width="16px" alt="copy" />
                                </span>
                              )}
                            </span>
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <span className={classes.cellWithCopy}>
                              <span className={classes.truncateText}>
                                {item.Email || "—"}
                              </span>
                              {item.Email && (
                                <span
                                  onClick={() => copy(item.Email)}
                                  className={classes.copyIcon}
                                >
                                  <img src={copyicon} width="16px" alt="copy" />
                                </span>
                              )}
                            </span>
                          </TableCell>

                          {/* Color column */}
                          <TableCell className={classes.tableCell}>
                            {item.Color ? (
                              <span className={classes.colorCell}>
                                <span
                                  className={classes.colorSwatch}
                                  style={{ backgroundColor: item.Color }}
                                />
                                <span className={classes.truncateText}>
                                  {item.Color}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: "rgba(210,210,225,0.35)" }}>
                                —
                              </span>
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            {item.Day || <span style={{ color: "rgba(210,210,225,0.35)" }}>—</span>}
                          </TableCell>
                          
                          <TableCell className={classes.tableCell}>
                            {item.Category || <span style={{ color: "rgba(210,210,225,0.35)" }}>—</span>}
                          </TableCell>

                          {/* QR Code column */}
                          <TableCell className={classes.tableCell}>
                            {item.qrCode || item.ticketId ? (
                              <Button
                                className={classes.qrButton}
                                onClick={() => handleShowQr(item)}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ marginRight: 5, flexShrink: 0 }}
                                >
                                  <rect x="3" y="3" width="7" height="7" />
                                  <rect x="14" y="3" width="7" height="7" />
                                  <rect x="3" y="14" width="7" height="7" />
                                  <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3" />
                                </svg>
                                Show QR
                              </Button>
                            ) : (
                              <span style={{ color: "rgba(210,210,225,0.35)" }}>
                                —
                              </span>
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <span
                              style={{
                                color: item.checkIn ? "#4ade80" : "#f87171",
                                fontWeight: 700,
                              }}
                            >
                              {item.checkIn ? "Yes" : "No"}
                            </span>
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <Button
                              style={{
                                backgroundColor: item?.checkIn
                                  ? "#2a2a3a"
                                  : "#e81b23",
                                border: item?.checkIn
                                  ? "1px solid rgba(255,255,255,0.15)"
                                  : "none",
                              }}
                              className={classes.button}
                              onClick={() =>
                                item?.checkIn
                                  ? checkOutUser(item)
                                  : saveQrCodeDetails(item)
                              }
                            >
                              {item?.checkIn ? "Check Out" : "Check In"}
                            </Button>
                            {/* <Button
                              style={{
                                backgroundColor: "#2a2a3a",
                                border: "1px solid rgba(255,255,255,0.15)",
                              }}
                              className={classes.button}
                              onClick={() => openPrintPreview(item)}
                            >
                              Print
                            </Button> */}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>

                  <Modal open={open} onClose={handleClose}>
                    <Box className={classes.customModal}>
                      <>
                        <TextField
                          label="Name"
                          value={Firstname}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                        <TextField
                          label="Registration Number"
                          value={RegistrationNumber}
                          onChange={(e) => setRegistrationNumber(e.target.value)}
                        />
                        <TextField
                          label="Mobile No"
                          value={MobileNo}
                          onChange={(e) => setMobileNo(e.target.value)}
                        />
                        <TextField
                          label="Email"
                          type="email"
                          value={Email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        <TextField
                          label="Color"
                          value={Color}
                          onChange={(e) => setColor(e.target.value)}
                        />
                        <TextField
                          label="Day"
                          value={Day}
                          onChange={(e) => setDay(e.target.value)}
                        />
                        <TextField
                          label="Category"
                          value={Category}
                          onChange={(e) => setCategory(e.target.value)}
                        />
                        <TextField
                          label="QR Code"
                          value={qrCode}
                          onChange={(e) => setQrCode(e.target.value)}
                        />
                        <Button
                          className={classes.btn}
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            saveQrCodeDetails({
                              _id: uid,
                              Firstname,
                              RegistrationNumber,
                              MobileNo,
                              Email,
                              color: Color,
                              Day,
                              Category,
                              qrCode,
                            })
                          }
                        >
                          Check In
                        </Button>
                        <Button
                          style={{ marginLeft: "2rem" }}
                          className={classes.btn}
                          variant="contained"
                          color="primary"
                          onClick={() => checkOutUser({ _id: uid })}
                        >
                          Check Out
                        </Button>
                      </>
                    </Box>
                  </Modal>
                </Table>
              </div>

              <div className={classes.pagination}>
                <Pagination
                  count={Math.ceil(filteredData.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "rgba(200,200,210,0.7)",
                      borderColor: "rgba(255,255,255,0.1)",
                      transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                      "&:hover": {
                        backgroundColor: "rgba(232,27,35,0.15)",
                        borderColor: "#e81b23",
                        color: "#fff",
                        transform: "scale(1.1)",
                      },
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#e81b23 !important",
                      color: "#fff !important",
                      fontWeight: 700,
                      boxShadow: "0 4px 16px rgba(232,27,35,0.5)",
                    },
                  }}
                />
              </div>
            </div>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Simplified QR Code Dialog ── */}
      <Modal open={qrDialogOpen} onClose={handleCloseQrDialog}>
        <Box className={classes.qrModal}>
          {/* Close button */}
          <button className={classes.qrCloseBtn} onClick={handleCloseQrDialog}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <p className={classes.qrTitle}>QR Code</p>
          {qrDialogData.name && (
            <p className={classes.qrName}>{qrDialogData.name}</p>
          )}

          {/* QR Image — rendered directly from stored data URI */}
          <div className={classes.qrImageWrap}>
            {qrDialogData.src && (
              <img
                src={qrDialogData.src}
                alt="QR Code"
                width={220}
                height={220}
                style={{ display: "block", borderRadius: 8 }}
              />
            )}
          </div>

          {/* Download button */}
          {qrDialogData.src && (
            <a
              href={qrDialogData.src}
              download={`qr-${qrDialogData.name || "code"}.png`}
              className={classes.downloadBtn}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                style={{ marginRight: 6 }}
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </a>
          )}
        </Box>
      </Modal>

      {/* ── Print Preview popup — same card layout used on ScanQr.js / GenerateQr.js:
          Name + secondary line (Mobile No • Email) on the left (auto-fit / wrap), QR
          on the right, edge-to-edge. This is a visual preview only — the actual print
          output comes from the portal-rendered .qr-print-target below, not from this
          div. ── */}
      <Modal open={printOpen} onClose={closePrintPreview}>
        <Box className={classes.printModal}>
          <button className={classes.qrCloseBtn} onClick={closePrintPreview}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <p className={classes.qrTitle}>Print Preview</p>

          <div
            style={{
              background: "#fff",
              color: "#000",
              borderRadius: "10px",
              padding: "15vh 20px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <PrintCardContent ticket={printData} qrSize={120} />
          </div>

          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            style={{ width: "100%" }}
            onClick={handlePrintNow}
          >
            Print
          </Button>
        </Box>
      </Modal>

      {/*
        ── ACTUAL print target, rendered via portal directly onto document.body ──

        Why a portal:
        The Print Preview Modal's Box has a `transform` left on it by the modal-in
        animation. Any ancestor with a `transform` becomes the containing block for
        `position: fixed` descendants — so if the print target lived inside that
        Box, its fixed positioning would be computed relative to the modal's
        on-screen center, not the actual printed page. Rendering this node straight
        onto <body> via a portal avoids that transformed ancestor entirely.

        Sizing: fixed to a 3.5in x 5in card (matching the ScanQr/GenerateQr print
        target and the label stock this is meant for), instead of stretching to
        100% of an arbitrary page.
      */}
      {printOpen &&
        ReactDOM.createPortal(
          <div
            className="qr-print-target"
            style={{
              background: "#fff",
              color: "#000",
              width: "3.5in",
              height: "5in",
              boxSizing: "border-box",
              padding: "12.5% 15px 20px 30px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <PrintCardContent ticket={printData} qrSize={150} />
          </div>,
          document.body,
        )}

      {/*
        Print-only styles:

        IMPORTANT — why this uses visibility/position instead of display:none:
        FitText (used inside PrintCardContent) measures its own rendered width via
        a ref to decide how much to shrink or wrap the Name/secondary-line text. An
        element with `display: none` has a width of 0, so if the print target were
        hidden that way, FitText would measure 0 and never shrink or wrap — it
        would render at max font size and hard-clip instead of matching the
        preview. Instead, the target stays in the layout at all times — sized to
        its real 3.5in x 5in dimensions — but is kept off-screen and invisible via
        `position: fixed` (removes it from the page flow) and `visibility: hidden`
        (invisible, but still measurable). During printing, only `visibility`
        flips to `visible` (and everything else flips to `hidden`), so the exact
        layout already measured on screen is what prints — no mismatch with the
        preview.

        NOTE: Browser-injected print headers/footers (page URL, "1 of 1", date/time)
        cannot be removed from code — they come from the browser's print dialog,
        not from this page's DOM. Disable them manually in the print dialog:
          Chrome:  More settings → uncheck "Headers and footers"
          Firefox: scroll print settings → "Headers and footers" → set all six
                   dropdowns to "--blank--"
      */}
      {printOpen && (
        <style>{`
          .qr-print-target {
            visibility: hidden;
            position: fixed;
            top: 0;
            left: 0;
          }
          @media print {
            @page { size: 3.5in 5in; margin: 0; }
            html, body {
              height: 100%;
              margin: 0;
            }
            body * { visibility: hidden; }
            .qr-print-target, .qr-print-target * { visibility: visible; }
          }
        `}</style>
      )}
    </Container>
  );
};

export default UserTable;

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes tableRowFade": {
    from: { opacity: 0, transform: "translateX(-12px)" },
    to: { opacity: 1, transform: "translateX(0)" },
  },
  "@keyframes orbFloat1": {
    "0%, 100%": { transform: "translate(0,0) scale(1)" },
    "33%": { transform: "translate(60px,-80px) scale(1.15)" },
    "66%": { transform: "translate(-40px,40px) scale(0.9)" },
  },
  "@keyframes orbFloat2": {
    "0%, 100%": { transform: "translate(0,0) scale(1)" },
    "33%": { transform: "translate(-70px,50px) scale(1.1)" },
    "66%": { transform: "translate(50px,-60px) scale(0.95)" },
  },
  "@keyframes orbFloat3": {
    "0%, 100%": { transform: "translate(0,0) scale(1)" },
    "50%": { transform: "translate(40px,60px) scale(1.2)" },
  },
  "@keyframes headerShimmer": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
  "@keyframes qrModalIn": {
    from: { opacity: 0, transform: "translate(-50%, -46%) scale(0.92)" },
    to: { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
  },

  conatiner: {
    background: "linear-gradient(160deg,#0a0a0f 0%,#12121a 40%,#0d0d15 100%)",
    height: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    position: "relative",
    padding: "0 !important",
    overflow: "hidden",
  },

  bgOrb1: {
    position: "fixed",
    top: "-15%",
    left: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(232,27,35,0.18) 0%,rgba(232,27,35,0.04) 50%,transparent 70%)",
    animation: "$orbFloat1 12s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(8px)",
  },
  bgOrb2: {
    position: "fixed",
    bottom: "-20%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(180,10,18,0.14) 0%,rgba(120,0,10,0.05) 50%,transparent 70%)",
    animation: "$orbFloat2 16s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(12px)",
  },
  bgOrb3: {
    position: "fixed",
    top: "40%",
    left: "30%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(255,80,80,0.06) 0%,transparent 70%)",
    animation: "$orbFloat3 20s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(20px)",
  },

  card: {
    background: "rgba(14,14,20,0.88)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow:
      "0 8px 64px 0 rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "99.6%",
    margin: "0 !important",
    padding: "0 !important",
    overflowY: "scroll",
    position: "relative",
    zIndex: 1,
    animation: "$fadeIn 0.7s cubic-bezier(0.22,1,0.36,1)",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.03)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg,#e81b23 0%,#7b0008 100%)",
      borderRadius: "10px",
    },
  },

  root: {
    width: "100%",
    marginTop: theme.spacing(2),
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(0, 1) },
  },

  searchRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down("xs")]: { gap: theme.spacing(1.5) },
  },

  search: {
    width: "22rem",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      flex: "1 1 auto",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.06)",
      borderRadius: "12px",
      color: "#f0f0f5",
      transition: "all 0.3s ease",
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.09)",
        "& fieldset": { borderColor: "rgba(232,27,35,0.5)" },
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(232,27,35,0.06)",
        boxShadow: "0 0 0 3px rgba(232,27,35,0.15)",
        "& fieldset": { borderColor: "#e81b23", borderWidth: "2px" },
      },
    },
    "& .MuiInputLabel-root": { color: "rgba(180,180,200,0.6)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#e81b23" },
    "& .MuiOutlinedInput-input": { color: "#f0f0f5" },
  },

  rowCount: {
    color: "rgba(180,180,200,0.5)",
    fontSize: "0.85rem",
    fontWeight: 500,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },

  tableWrapper: {
    borderRadius: "16px",
    overflow: "auto",
    border: "1px solid rgba(255,255,255,0.07)",
    boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
    WebkitOverflowScrolling: "touch",
    "&::-webkit-scrollbar": { height: "5px" },
    "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(90deg,#e81b23,#7b0008)",
      borderRadius: "10px",
    },
  },

  table: { minWidth: 750, borderCollapse: "collapse" },

  tableHeader: {
    background: "linear-gradient(135deg,#c0111a 0%,#e81b23 50%,#c0111a 100%)",
    backgroundSize: "200% 200%",
    animation: "$headerShimmer 5s ease infinite",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.8rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.72rem",
      padding: "12px 10px",
    },
    padding: "14px 16px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderRight: "1px solid rgba(255,255,255,0.15)",
    borderBottom: "none",
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 2,
    textShadow: "0 1px 3px rgba(0,0,0,0.3)",
    whiteSpace: "nowrap",
    "&:last-child": { borderRight: "none" },
  },

  tableRow: {
    backgroundColor: "rgba(16,16,24,0.9)",
    transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
    animation: "$tableRowFade 0.45s cubic-bezier(0.22,1,0.36,1) backwards",
    willChange: "transform",
    "&:nth-child(even)": { backgroundColor: "rgba(22,22,32,0.9)" },
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.08)",
      transform: "translateX(3px)",
      boxShadow: "inset 3px 0 0 #e81b23,0 2px 16px rgba(232,27,35,0.15)",
      "& $tableCell": { color: "#ffffff" },
    },
  },

  tableCell: {
    color: "rgba(210,210,225,0.85)",
    padding: "12px 16px",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 10px",
      fontSize: "0.78rem",
    },
    fontSize: "0.875rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    borderRight: "1px solid rgba(255,255,255,0.05)",
    transition: "color 0.25s ease",
    textAlign: "center",
    whiteSpace: "nowrap",
    "&:last-child": { borderRight: "none" },
  },

  cellWithCopy: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: "160px",
    [theme.breakpoints.down("sm")]: { maxWidth: "110px" },
  },

  // ── Color column: small swatch + label ──
  colorCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: "140px",
  },

  colorSwatch: {
    width: "14px",
    height: "14px",
    borderRadius: "4px",
    flexShrink: 0,
    border: "1px solid rgba(255,255,255,0.25)",
  },

  truncateText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },

  monoText: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    maxWidth: "120px",
    [theme.breakpoints.down("sm")]: { maxWidth: "80px", fontSize: "0.72rem" },
  },

  copyIcon: {
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
    opacity: 0.45,
    "&:hover": {
      opacity: 1,
      transform: "scale(1.2) translateY(-1px)",
      filter: "drop-shadow(0 0 4px rgba(232,27,35,0.6))",
    },
    "& img": { filter: "brightness(0) invert(1)" },
  },

  button: {
    margin: theme.spacing(0.5),
    color: "#fff",
    fontWeight: 600,
    borderRadius: "8px",
    padding: "6px 12px",
    [theme.breakpoints.down("sm")]: { padding: "5px 8px", fontSize: "0.72rem" },
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    textTransform: "none",
    fontSize: "0.82rem",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    "&:hover": {
      filter: "brightness(1.2)",
      transform: "translateY(-2px) scale(1.04)",
      boxShadow: "0 6px 20px rgba(232,27,35,0.4)",
    },
  },

  qrButton: {
    color: "rgba(210,210,225,0.85)",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "8px",
    padding: "5px 12px",
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.02em",
    display: "inline-flex",
    alignItems: "center",
    transition: "all 0.28s cubic-bezier(0.22,1,0.36,1)",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.15)",
      borderColor: "rgba(232,27,35,0.5)",
      color: "#fff",
      transform: "translateY(-2px) scale(1.04)",
      boxShadow: "0 4px 16px rgba(232,27,35,0.3)",
    },
  },

  // ── Simplified QR Modal ──
  qrModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    animation: "$qrModalIn 0.3s cubic-bezier(0.22,1,0.36,1) forwards",
    background: "#0f0f17",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
    padding: "1.75rem",
    width: "min(300px, 88vw)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },

  // ── Print Preview Modal ──
  printModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    animation: "$qrModalIn 0.3s cubic-bezier(0.22,1,0.36,1) forwards",
    background: "#0f0f17",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
    padding: "1.75rem",
    width: "min(340px, 90vw)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },

  qrCloseBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "rgba(210,210,225,0.6)",
    transition: "all 0.2s ease",
    padding: 0,
    "&:hover": {
      background: "rgba(232,27,35,0.18)",
      borderColor: "rgba(232,27,35,0.4)",
      color: "#fff",
      transform: "rotate(90deg)",
    },
  },

  qrTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    margin: 0,
    letterSpacing: "0.04em",
  },

  qrName: {
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.78rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  // White-padded box so QR is scannable against dark background
  qrImageWrap: {
    background: "#fff",
    borderRadius: "10px",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  },

  downloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    color: "rgba(210,210,225,0.7)",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px",
    padding: "7px 16px",
    fontSize: "0.78rem",
    fontWeight: 600,
    textDecoration: "none",
    letterSpacing: "0.03em",
    transition: "all 0.22s ease",
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.15)",
      borderColor: "rgba(232,27,35,0.4)",
      color: "#fff",
    },
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
  },

  btn: {
    marginTop: 10,
    marginBottom: 20,
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    boxShadow: "0 4px 16px rgba(232,27,35,0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-3px)",
      boxShadow: "0 8px 24px rgba(232,27,35,0.5)",
    },
  },

  customModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    padding: "2rem",
    [theme.breakpoints.down("xs")]: { padding: "1.5rem 1.25rem" },
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    borderRadius: "20px",
    boxShadow:
      "0 24px 64px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.08),inset 0 1px 0 rgba(255,255,255,0.06)",
    border: "1px solid rgba(232,27,35,0.2)",
    animation: "$slideDown 0.4s cubic-bezier(0.22,1,0.36,1)",
    width: "min(500px, 90vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    boxSizing: "border-box",
    "& .MuiTextField-root": {
      marginBottom: "16px",
      width: "100%",
      "& .MuiInputBase-input": { color: "#f0f0f5" },
      "& .MuiInputLabel-root": { color: "rgba(180,180,200,0.6)" },
      "& .MuiOutlinedInput-root": {
        backgroundColor: "rgba(255,255,255,0.06)",
        borderRadius: "10px",
        "&:hover fieldset": { borderColor: "rgba(232,27,35,0.5)" },
        "&.Mui-focused fieldset": {
          borderColor: "#e81b23",
          borderWidth: "2px",
        },
        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
      },
      "& .MuiInputLabel-root.Mui-focused": { color: "#e81b23" },
    },
  },
}));